import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";
import {
  Rss, Sparkles, Loader2, Send, Trash2, CalendarClock, Clock, X, Image as ImageIcon,
  RefreshCw, Upload, ExternalLink, Lightbulb, Plus, Link2, Globe, Bell, CheckCircle2, AlertCircle, ChevronDown,
} from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-amber-accent/10 text-amber-accent border-amber-accent/40",
  scheduled: "bg-signal-500/10 text-signal-500 border-signal-500/40",
  publishing: "bg-signal-500/10 text-signal-500 border-signal-500/40",
  published: "bg-emerald-400/10 text-emerald-400 border-emerald-400/40",
  failed: "bg-red-500/10 text-red-400 border-red-500/40",
};

function fmtWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
function nextSlot() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
}

export default function BlogPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("Expert's comment");
  const [composing, setComposing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/blog");
      setData(data);
    } catch { toast.error("Failed to load Blog studio"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const compose = async () => {
    setComposing(true);
    try {
      await api.post("/blog/compose", { topic: topic.trim(), category });
      toast.success("Draft created — writing the cover image now");
      setTopic("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft the post");
    }
    setComposing(false);
  };

  const draftFromIdeas = async (ideas) => {
    await api.post("/blog/compose", { category, ideas });
    await load();
    document.querySelector('[data-testid="blog-posts-heading"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const categories = data?.categories || ["Expert's comment"];
  const connection = data?.connection || { connected: false };
  const posts = data?.posts || [];

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="blog-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase">Content</div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><Rss className="w-7 h-7 text-signal-500" /> Website Blog</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              AI-draft SEO blog posts for your company website, schedule them, and publish straight to
              your blog. Pull topic ideas from competitors' blogs.
            </p>
          </div>
          <ConnectionBadge connection={connection} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Composer */}
          <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="w-4 h-4 text-signal-500" /> Draft a new blog post with AI</div>
            <div>
              <label className="text-xs text-muted-foreground">Topic (optional — leave blank for a trending idea)</label>
              <Input
                data-testid="blog-topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Bonded warehousing at the Port of Koper"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Category</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {categories.map((c) => (
                  <button
                    key={c}
                    data-testid={`blog-cat-${c}`}
                    onClick={() => setCategory(c)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${category === c ? "bg-signal-500 text-white border-signal-500" : "border-border text-muted-foreground hover:border-signal-500/60"}`}
                  >{c}</button>
                ))}
              </div>
            </div>
            <Button data-testid="blog-compose-btn" onClick={compose} disabled={composing} className="w-full">
              {composing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Draft with AI</>}
            </Button>
          </div>

          {/* Reminder */}
          <ReminderCard reminder={data?.reminder} connected={connection.connected} onChanged={load} />
        </div>

        {/* Competitor blogs → ideas */}
        <CompetitorPanel
          competitors={data?.competitors || []}
          onChanged={load}
          onUseIdea={(idea) => { setTopic(idea.title); if (idea.category) setCategory(idea.category); window.scrollTo({ top: 0, behavior: "smooth" }); toast.success("Idea loaded into the composer"); }}
          onDraftFromIdeas={draftFromIdeas}
        />

        {/* Posts */}
        <div className="space-y-4">
          <div className="text-sm font-semibold text-muted-foreground" data-testid="blog-posts-heading">Your blog posts</div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : posts.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center text-sm text-muted-foreground" data-testid="blog-empty">
              No blog posts yet. Draft your first one above.
            </div>
          ) : (
            posts.map((p) => (
              <BlogPostCard key={p.id} post={p} categories={categories} connected={connection.connected} onChanged={load} />
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function ConnectionBadge({ connection }) {
  if (connection.connected) {
    return (
      <div data-testid="blog-connection-badge" className="flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-400/40 bg-emerald-400/10 text-emerald-400 text-xs font-medium">
        <CheckCircle2 className="w-4 h-4" /> Connected to website blog
      </div>
    );
  }
  return (
    <div data-testid="blog-connection-badge" className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-muted text-muted-foreground text-xs font-medium max-w-xs">
      <AlertCircle className="w-4 h-4 shrink-0" />
      <span>Not connected — this workspace has no website blog. Drafting works; publishing is disabled.</span>
    </div>
  );
}

function ReminderCard({ reminder, connected, onChanged }) {
  const [saving, setSaving] = useState(false);
  const r = reminder || { enabled: true, cadence: "weekly", email_to: "" };
  const save = async (updates) => {
    setSaving(true);
    try { await api.post("/blog/reminder", updates); await onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save reminder"); }
    setSaving(false);
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-testid="blog-reminder-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><Bell className="w-4 h-4 text-signal-500" /> Blog reminder</div>
        <Switch data-testid="blog-reminder-toggle" checked={!!r.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} />
      </div>
      <p className="text-xs text-muted-foreground">Get an email nudge when no blog post is drafted for the period.</p>
      <div>
        <label className="text-xs text-muted-foreground">Cadence</label>
        <div className="flex gap-2 mt-1">
          {["weekly", "biweekly", "monthly"].map((c) => (
            <button key={c} data-testid={`blog-cadence-${c}`} disabled={saving}
              onClick={() => save({ cadence: c })}
              className={`px-2.5 py-1 rounded-full text-[11px] border capitalize ${r.cadence === c ? "bg-signal-500 text-white border-signal-500" : "border-border text-muted-foreground"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Email</label>
        <Input data-testid="blog-reminder-email" defaultValue={r.email_to} disabled={saving}
          onBlur={(e) => { if (e.target.value.trim() && e.target.value.trim() !== r.email_to) save({ email_to: e.target.value.trim() }); }}
          placeholder="you@company.com" className="mt-1 h-8 text-xs" />
      </div>
      {!connected && <p className="text-[11px] text-amber-accent">Reminders only send for workspaces with a connected website blog.</p>}
    </div>
  );
}

function CompetitorPanel({ competitors, onChanged, onUseIdea, onDraftFromIdeas }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [ideasMap, setIdeasMap] = useState({});     // { [competitorId]: ideasResult } — persists across blogs
  const [loadingMap, setLoadingMap] = useState({}); // { [competitorId]: bool }
  const [openMap, setOpenMap] = useState({});       // { [competitorId]: bool } — dropdown expanded
  const [allLoading, setAllLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const add = async () => {
    if (!url.trim()) { toast.error("Paste a blog URL"); return; }
    setAdding(true);
    try {
      await api.post("/blog/competitors", { url: url.trim(), name: name.trim() });
      toast.success("Competitor blog added");
      setUrl(""); setName("");
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail === "invalid_url" ? "That doesn't look like a valid URL" : "Could not add"); }
    setAdding(false);
  };
  const remove = async (id) => {
    try {
      await api.delete(`/blog/competitors/${id}`);
      setIdeasMap((m) => { const n = { ...m }; delete n[id]; return n; });
      setOpenMap((m) => { const n = { ...m }; delete n[id]; return n; });
      await onChanged();
    } catch { toast.error("Failed"); }
  };

  // Fetch ideas for ONE competitor without wiping any other competitor's ideas.
  const fetchIdeas = async (c) => {
    setLoadingMap((m) => ({ ...m, [c.id]: true }));
    try {
      const { data } = await api.post("/blog/competitors/ideas", { url: c.url });
      setIdeasMap((m) => ({ ...m, [c.id]: data }));
      setOpenMap((m) => ({ ...m, [c.id]: true }));
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail === "could_not_read_competitor" ? `Couldn't read ${c.name || c.url}` : "Could not get ideas");
      return null;
    } finally {
      setLoadingMap((m) => ({ ...m, [c.id]: false }));
    }
  };

  const getIdeas = async (c) => { await fetchIdeas(c); };
  const toggleOpen = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));

  const getIdeasAll = async () => {
    if (competitors.length === 0) return;
    setAllLoading(true);
    let got = 0;
    for (const c of competitors) {
      const d = await fetchIdeas(c);       // sequential — keeps every result, opens each dropdown
      if (d?.ideas?.length) got += 1;
    }
    setAllLoading(false);
    toast[got ? "success" : "error"](got ? `Got ideas from ${got} blog${got > 1 ? "s" : ""}` : "Couldn't read any of the blogs");
  };

  // All ideas across every fetched blog, flattened + de-duplicated by title.
  const allIdeas = (() => {
    const seen = new Set(); const out = [];
    for (const res of Object.values(ideasMap)) {
      for (const idea of res?.ideas || []) {
        const key = (idea.title || "").toLowerCase().trim();
        if (key && !seen.has(key)) { seen.add(key); out.push(idea); }
      }
    }
    return out;
  })();

  const draftEuropacific = async () => {
    if (allIdeas.length === 0) { toast.error("Get some ideas first"); return; }
    setDrafting(true);
    try {
      await onDraftFromIdeas(allIdeas);
      toast.success(`Drafting a post from ${allIdeas.length} ideas across your competitor blogs`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft the post"); }
    setDrafting(false);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="blog-competitors-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold"><Lightbulb className="w-4 h-4 text-signal-500" /> Competitor blogs → topic ideas</div>
        {competitors.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="blog-get-ideas-all" size="sm" variant="outline" onClick={getIdeasAll} disabled={allLoading || drafting}>
              {allLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Reading all…</> : <><Lightbulb className="w-3.5 h-3.5 mr-1" /> Get ideas from all blogs</>}
            </Button>
            {allIdeas.length > 0 && (
              <Button data-testid="blog-draft-from-ideas" size="sm" onClick={draftEuropacific} disabled={drafting || allLoading}>
                {drafting ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Drafting…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Draft a post from all ideas ({allIdeas.length})</>}
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="blog-competitor-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://competitor.com/blog" className="flex-1" />
        <Input data-testid="blog-competitor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="sm:w-40" />
        <Button data-testid="blog-competitor-add" onClick={add} disabled={adding} variant="secondary">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
        </Button>
      </div>
      {competitors.length === 0 ? (
        <p className="text-xs text-muted-foreground">Add a competitor's blog (e.g. https://suus.com/en/resources-insights/blog) to mine differentiated topic ideas.</p>
      ) : (
        <div className="space-y-2">
          {competitors.map((c) => {
            const res = ideasMap[c.id];
            const isLoading = !!loadingMap[c.id];
            const isOpen = !!openMap[c.id];
            const count = res?.ideas?.length || 0;
            return (
            <div key={c.id} className="border border-border rounded-xl p-3" data-testid={`blog-competitor-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
                <a href={c.url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:text-signal-500 truncate max-w-md">{c.name || c.url}</a>
                <a href={c.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-signal-500"><ExternalLink className="w-3.5 h-3.5" /></a>
                {count > 0 && !isOpen && <span className="text-[11px] text-signal-500">{count} ideas ready</span>}
                <div className="ml-auto flex items-center gap-2">
                  {res ? (
                    <button data-testid={`blog-competitor-toggle-${c.id}`} onClick={() => toggleOpen(c.id)}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:border-signal-500/60">
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} /> {isOpen ? "Hide" : "Show"} ideas
                    </button>
                  ) : null}
                  <Button data-testid={`blog-competitor-ideas-${c.id}`} size="sm" variant="outline" onClick={() => getIdeas(c)} disabled={isLoading}>
                    {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Lightbulb className="w-3.5 h-3.5 mr-1" /> {res ? "Refresh" : "Get ideas"}</>}
                  </Button>
                  <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-red-400" data-testid={`blog-competitor-remove-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {res && isOpen && (
                <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid={`blog-ideas-list-${c.id}`}>
                  {res.competitor_themes?.length > 0 && (
                    <div className="text-[11px] text-muted-foreground">They write about: {res.competitor_themes.join(" · ")}</div>
                  )}
                  {(res.ideas || []).map((idea, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 bg-muted/40 rounded-lg p-2.5">
                      <div>
                        <div className="text-sm font-medium">{idea.title}</div>
                        <div className="text-[11px] text-muted-foreground">{idea.category} — {idea.angle}</div>
                      </div>
                      <Button size="sm" variant="ghost" data-testid={`blog-use-idea-${c.id}-${i}`} onClick={() => onUseIdea(idea)} className="shrink-0">Use</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BlogPostCard({ post, categories, connected, onChanged }) {
  const [title, setTitle] = useState(post.title || "");
  const [body, setBody] = useState(post.body || "");
  const [category, setCategory] = useState(post.category || "Expert's comment");
  const [busy, setBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgGenerating, setImgGenerating] = useState(post.image_status === "generating");
  const [scheduling, setScheduling] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(nextSlot());
  const [imgGuidance, setImgGuidance] = useState("");

  const dirty = title !== (post.title || "") || body !== (post.body || "") || category !== (post.category || "");
  const isPublished = post.status === "published";

  useEffect(() => {
    let timer, active = true;
    const fetchImg = async () => {
      try {
        const r = await api.get(`/blog/${post.id}/image`, { responseType: "blob" });
        if (active) { setImgUrl(URL.createObjectURL(r.data)); setImgGenerating(false); }
        return true;
      } catch { return false; }
    };
    (async () => {
      if (post.image_status === "ready") { await fetchImg(); return; }
      if (post.image_status === "generating") {
        setImgGenerating(true);
        let n = 0;
        timer = setInterval(async () => {
          n += 1;
          const ok = await fetchImg();
          if (ok || n > 24) { clearInterval(timer); if (!ok && n > 24) setImgGenerating(false); }
        }, 5000);
      }
    })();
    return () => { active = false; if (timer) clearInterval(timer); };
  }, [post.id, post.image_status]);

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/blog/${post.id}`, { title, body, category }); toast.success("Saved"); await onChanged(); }
    catch { toast.error("Save failed"); }
    setBusy(false);
  };
  const regenImage = async () => {
    setImgGenerating(true); setImgUrl(null);
    try {
      await api.post(`/blog/${post.id}/regenerate-image`, { image_guidance: imgGuidance.trim() });
      let n = 0;
      const t = setInterval(async () => {
        n += 1;
        try { const r = await api.get(`/blog/${post.id}/image`, { responseType: "blob" }); setImgUrl(URL.createObjectURL(r.data)); setImgGenerating(false); clearInterval(t); }
        catch { if (n > 24) { clearInterval(t); setImgGenerating(false); } }
      }, 5000);
    } catch { toast.error("Could not regenerate image"); setImgGenerating(false); }
  };
  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    setImgGenerating(true); setImgUrl(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/blog/${post.id}/upload-image`, { image_base64: reader.result });
        const r = await api.get(`/blog/${post.id}/image`, { responseType: "blob", params: { t: Date.now() } });
        setImgUrl(URL.createObjectURL(r.data)); setImgGenerating(false); toast.success("Photo uploaded");
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); setImgGenerating(false); }
    };
    reader.readAsDataURL(f);
  };
  const publish = async () => {
    if (dirty) await save();
    setBusy(true);
    try {
      const { data } = await api.post(`/blog/${post.id}/publish`);
      toast.success("Published to website blog", { description: data.blog_url ? `Live at ${data.blog_url}` : "The site will rebuild shortly." });
      await onChanged();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "blog_not_configured" ? "No website blog connected for this workspace." : err === "invalid_title" ? "Give the post a real title first." : (err || "Publish failed"));
    }
    setBusy(false);
  };
  const doSchedule = async () => {
    if (!scheduleAt) { toast.error("Pick a date & time"); return; }
    if (new Date(scheduleAt).getTime() <= Date.now()) { toast.error("Pick a time in the future"); return; }
    if (dirty) await save();
    setBusy(true);
    try {
      const { data } = await api.post(`/blog/${post.id}/schedule`, { scheduled_at: new Date(scheduleAt).toISOString() });
      toast.success(`Scheduled for ${fmtWhen(data.scheduled_at)}`); setScheduling(false); await onChanged();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "blog_not_configured" ? "No website blog connected for this workspace." : err === "scheduled_time_in_past" ? "Pick a time in the future." : (err || "Could not schedule"));
    }
    setBusy(false);
  };
  const unschedule = async () => { setBusy(true); try { await api.post(`/blog/${post.id}/unschedule`); await onChanged(); } catch { toast.error("Failed"); } setBusy(false); };
  const dismiss = async () => { setBusy(true); try { await api.post(`/blog/${post.id}/dismiss`); toast.success("Dismissed"); await onChanged(); } catch { toast.error("Failed"); } setBusy(false); };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid={`blog-post-${post.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className={`px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wide ${STATUS_STYLE[post.status] || STATUS_STYLE.draft}`} data-testid={`blog-post-status-${post.id}`}>{post.status}</span>
        <span className="px-2 py-0.5 rounded-full border border-border text-muted-foreground">{category}</span>
        {post.source === "ai_draft" && <span className="text-muted-foreground">· AI-drafted</span>}
        {post.scheduled_at && post.status === "scheduled" && <span className="text-signal-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {fmtWhen(post.scheduled_at)}</span>}
        {post.blog_url && <a href={post.blog_url} target="_blank" rel="noreferrer" className="text-emerald-400 flex items-center gap-1 hover:underline"><ExternalLink className="w-3 h-3" /> View live</a>}
        <span className="ml-auto text-muted-foreground">{relativeTime(post.created_at)}</span>
      </div>

      {(imgUrl || imgGenerating) && (
        <div className="rounded-xl overflow-hidden bg-muted/40 border border-border aspect-[16/7] flex items-center justify-center">
          {imgGenerating ? <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Generating cover image…</div>
            : <img src={imgUrl} alt="cover" className="w-full h-full object-cover" />}
        </div>
      )}

      <Input data-testid={`blog-title-${post.id}`} value={title} onChange={(e) => setTitle(e.target.value)} disabled={isPublished} className="font-semibold text-base" />
      <div className="flex flex-wrap gap-1.5">
        {categories.map((c) => (
          <button key={c} disabled={isPublished} onClick={() => setCategory(c)}
            className={`px-2 py-0.5 rounded-full text-[11px] border ${category === c ? "bg-signal-500 text-white border-signal-500" : "border-border text-muted-foreground"}`}>{c}</button>
        ))}
      </div>
      <Textarea data-testid={`blog-body-${post.id}`} value={body} onChange={(e) => setBody(e.target.value)} disabled={isPublished} rows={10} className="text-sm leading-relaxed" />

      {post.blog_error && post.status !== "published" && <div className="text-xs text-red-400" data-testid={`blog-error-${post.id}`}>Last error: {post.blog_error}</div>}

      {!isPublished && (
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={imgGuidance} onChange={(e) => setImgGuidance(e.target.value)} placeholder="Describe a cover image (optional)" className="h-8 text-xs flex-1 min-w-[180px]" />
          <Button size="sm" variant="outline" onClick={regenImage} disabled={imgGenerating} data-testid={`blog-regen-image-${post.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" /> {imgUrl ? "Regenerate" : "Add image"}</Button>
          <label className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-border cursor-pointer hover:bg-muted" data-testid={`blog-upload-image-${post.id}`}>
            <Upload className="w-3.5 h-3.5" /> Upload
            <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
          </label>
        </div>
      )}

      {!isPublished && (
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border">
          {dirty && <Button size="sm" variant="secondary" onClick={save} disabled={busy} data-testid={`blog-save-${post.id}`}>Save</Button>}
          {post.status === "scheduled" ? (
            <Button size="sm" variant="outline" onClick={unschedule} disabled={busy} data-testid={`blog-unschedule-${post.id}`}><X className="w-3.5 h-3.5 mr-1" /> Unschedule</Button>
          ) : (
            <>
              <Button size="sm" onClick={publish} disabled={busy || !connected} data-testid={`blog-publish-${post.id}`} title={!connected ? "No website blog connected" : ""}>
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Publish to website
              </Button>
              <Button size="sm" variant="outline" onClick={() => setScheduling((s) => !s)} disabled={!connected} data-testid={`blog-schedule-toggle-${post.id}`}><CalendarClock className="w-3.5 h-3.5 mr-1" /> Schedule</Button>
            </>
          )}
          <button onClick={dismiss} disabled={busy} className="ml-auto text-muted-foreground hover:text-red-400" data-testid={`blog-dismiss-${post.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      )}

      {scheduling && !isPublished && post.status !== "scheduled" && (
        <div className="flex items-center gap-2 flex-wrap bg-muted/40 rounded-lg p-3" data-testid={`blog-schedule-panel-${post.id}`}>
          <Clock className="w-4 h-4 text-muted-foreground" />
          <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="bg-background border border-border rounded-md px-2 py-1 text-sm" data-testid={`blog-schedule-input-${post.id}`} />
          <Button size="sm" onClick={doSchedule} disabled={busy} data-testid={`blog-schedule-confirm-${post.id}`}>Confirm</Button>
        </div>
      )}

      {!connected && !isPublished && (
        <p className="text-[11px] text-amber-accent flex items-center gap-1"><Globe className="w-3 h-3" /> Connect a website blog (Settings → Blog / SEO ingestion) to publish or schedule. This workspace has no website blog.</p>
      )}
    </div>
  );
}
